/* ==========================================================================
   Exhaust Pipe Diameter Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-epc]');
  if (!root) return;

  /* Exhaust volume flow scales with power. Around 2.2 CFM per horsepower is
     the widely used figure for a naturally aspirated gasoline engine. */
  var CFM_PER_HP = 2.2;

  /* Target gas velocity in feet per minute. Higher velocity preserves low end
     scavenging, lower velocity reduces peak restriction. */
  var VELOCITY = { street: 17000, mixed: 15000, race: 13000 };

  /* A turbo drops exhaust pressure and temperature across the turbine, so the
     downstream pipe can be larger without hurting low end response. */
  var TURBO_AREA_FACTOR = 1.25;

  /* A crossover lets the two banks share, which effectively increases capacity. */
  var CROSSOVER_FACTOR = 0.92;

  var COMMON_SIZES = [1.75, 2.0, 2.25, 2.5, 2.75, 3.0, 3.5, 4.0, 4.5, 5.0];

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function compute(input) {
    if (!isFinite(input.hp) || input.hp <= 0) {
      return { ok: false, errors: ['Enter peak horsepower.'] };
    }
    var vel = VELOCITY[input.use] || VELOCITY.street;
    var cfm = input.hp * CFM_PER_HP;

    var pipes = input.config === 'single' ? 1 : 2;
    var totalAreaSqIn = (cfm / vel) * 144;
    if (input.turbo) totalAreaSqIn *= TURBO_AREA_FACTOR;
    if (input.config === 'dualx') totalAreaSqIn *= CROSSOVER_FACTOR;

    var perPipeArea = totalAreaSqIn / pipes;
    var idealDia = Math.sqrt((4 * perPipeArea) / Math.PI);

    /* Round to the nearest size you can actually buy. */
    var pick = COMMON_SIZES[0], gap = Infinity;
    COMMON_SIZES.forEach(function (s) {
      var g = Math.abs(s - idealDia);
      if (g < gap) { pick = s; gap = g; }
    });

    var pickArea = Math.PI * Math.pow(pick / 2, 2) * pipes;
    var actualVel = (cfm / (pickArea / 144));

    var status;
    if (actualVel > 20000) {
      status = { key: 'soon', label: num(pick, 2) + ' in',
                 note: 'Gas velocity is high at ' + comma(Math.round(actualVel)) + ' ft/min. Good low end, but restrictive at peak power.' };
    } else if (actualVel < 11000) {
      status = { key: 'soon', label: num(pick, 2) + ' in',
                 note: 'Gas velocity is low at ' + comma(Math.round(actualVel)) + ' ft/min. Expect soft low end response.' };
    } else {
      status = { key: 'good', label: num(pick, 2) + ' in' + (pipes === 2 ? ' each side' : ''),
                 note: comma(Math.round(actualVel)) + ' ft/min gas velocity, inside the useful range.' };
    }

    var warnings = [];
    if (input.config !== 'single' && input.hp < 250) {
      warnings.push('True duals on a modest power engine usually cost more low end than they gain anywhere. A single well sized pipe often works better below 250 hp.');
    }

    var rows = [
      { head: 'Sizing' },
      { label: 'Exhaust flow', value: comma(Math.round(cfm)) + ' CFM' },
      { label: 'Target velocity', value: comma(vel) + ' ft/min' },
      { label: 'Total area needed', value: num(totalAreaSqIn, 2) + ' sq in' },
      { label: 'Per pipe', value: num(perPipeArea, 2) + ' sq in' },
      { label: 'Ideal diameter', value: num(idealDia, 2) + ' in' },
      { label: 'Nearest real size', value: num(pick, 2) + ' in' },
      { head: 'At ' + num(pick, 2) + ' inch' },
      { label: 'Actual velocity', value: comma(Math.round(actualVel)) + ' ft/min' },
      { label: 'Total flow area', value: num(pickArea, 2) + ' sq in' },
      { head: 'Other sizes' }
    ];
    COMMON_SIZES.forEach(function (s) {
      if (s < idealDia - 0.8 || s > idealDia + 1.2) return;
      var a = Math.PI * Math.pow(s / 2, 2) * pipes;
      rows.push({ label: num(s, 2) + ' in' + (pipes === 2 ? ' x2' : ''),
                  value: comma(Math.round(cfm / (a / 144))) + ' ft/min' });
    });

    return {
      ok: true, errors: [], warnings: warnings, status: status,
      figures: [
        { label: 'Recommended tubing', value: num(pick, 2) + ' in',
          sub: pipes === 2 ? 'Each of two pipes.' : 'Single pipe.' },
        { label: 'Gas velocity', value: comma(Math.round(actualVel)) + ' ft/min',
          sub: 'Useful range is roughly 11,000 to 20,000 ft/min.' }
      ],
      rows: rows
    };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "hp",
          "key": "p",
          "type": "number"
      },
      {
          "name": "config",
          "key": "c",
          "type": "select"
      },
      {
          "name": "use",
          "key": "u",
          "type": "select"
      },
      {
          "name": "turbo",
          "key": "t",
          "type": "checkbox"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-epc__figure">'
        + '<div class="pc-epc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-epc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-epc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-epc__row pc-epc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-epc__row">'
        + '<span class="pc-epc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-epc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_EPC = { compute: compute };
})();
